import { useState, type FC } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify/unstyled';

import { useGetArtifactsQuery, useUploadStandaloneArtifactMutation } from '@features/integrations/api/create-agent.api';
import type { ArtifactsResponse } from '@features/integrations/api/create-agent.types';
import { GenericButton } from '@shared/components/ui/custom';
import { GenericFileUpload } from '@shared/components/ui/custom/GenericFileUpload';
import { FormControl, FormField, FormItem, FormMessage } from '@shared/components/ui/form';
import { LoadingPageSpinner } from '@shared/overlays';
import { ModalOverlay } from '@shared/overlays/error-overlays/ModalOverlay';
import type { EmbeddedErrorPayload } from '@shared/utils/api.types';

import { getHasRequiredArtifacts } from '../../create-agent.utils';
import { ArtifactsFoundSuccess } from '../general/create-agent-header.utils';

import type { ArtifactUploadFormValues } from './sap-artifact-upload.schema';
import { artifactUploadSchema } from './sap-artifact-upload.schema';

interface SapArtifactUploadProps {
  onProceedWithExistingArtifacts: (artifactsData?: ArtifactsResponse) => void;
  onUploadArtifactSuccess: (rawFile: File) => void;
  stagedFile: File[];
  onClose: () => void;
}

export const SapArtifactUpload: FC<SapArtifactUploadProps> = ({
  onProceedWithExistingArtifacts,
  onUploadArtifactSuccess,
  onClose,
  stagedFile,
}) => {
  const [uploadNewArtifact, setUploadNewArtifact] = useState(false);
  const [uploadStandaloneArtifact, { isLoading: isUploading, reset }] = useUploadStandaloneArtifactMutation();

  const { data: artifacts, isLoading: isLoadingArtifacts, isError, refetch } = useGetArtifactsQuery();

  const hasRequiredArtifacts = getHasRequiredArtifacts(artifacts?.content);

  const showUploadArtifact = !isLoadingArtifacts && (!hasRequiredArtifacts || uploadNewArtifact);
  const showFoundArtifacts = !isLoadingArtifacts && hasRequiredArtifacts && !uploadNewArtifact;

  const showCurrentArtifacts = hasRequiredArtifacts && uploadNewArtifact;

  const form = useForm<ArtifactUploadFormValues>({
    resolver: zodResolver(artifactUploadSchema),
    defaultValues: {
      artifactFile: stagedFile ?? [],
    },
  });

  if (isError) {
    return <ModalOverlay isError={true} refetch={refetch} />;
  }

  const onUploadStandaloneArtifacts = async (data: ArtifactUploadFormValues) => {
    const rawFileObject = data.artifactFile?.[0];

    if (!rawFileObject) {
      toast.error('Please select an SAP library artifact file to upload.');
      return;
    }

    try {
      await uploadStandaloneArtifact({
        file: rawFileObject,
      }).unwrap();

      onUploadArtifactSuccess(rawFileObject);
    } catch (error) {
      const rtkError = error as EmbeddedErrorPayload;

      console.error('Error uploading SAP artifact:', rtkError);

      const embeddedError = rtkError.data?._embedded?.errors?.[0]?.message;
      const errorMessage =
        embeddedError ?? rtkError.data?.message ?? 'Failed to upload the SAP artifact. Please try again.';

      form.setError('artifactFile', {
        type: 'server',
        message: errorMessage,
      });
    }
  };

  const handleCloseModal = () => {
    reset();
    onClose();
  };

  return (
    <FormProvider {...form}>
      <form className="space-y-6" noValidate onSubmit={form.handleSubmit(onUploadStandaloneArtifacts)}>
        {showUploadArtifact && (
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="artifactFile"
              render={({ field: { value, onChange } }) => (
                <FormItem className="space-y-1 max-w-[640px]">
                  <FormControl>
                    <GenericFileUpload
                      value={Array.isArray(value) ? value : []}
                      onChange={onChange}
                      allowedExtensions={['.zip']}
                      multiple={false}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex flex-col gap-0.5 pl-0.5">
              <div className="text-sm text-muted-foreground tracking-tight leading-relaxed">
                Click 'Upload' below to validate and proceed to next step.
              </div>
            </div>
          </div>
        )}

        {showFoundArtifacts && <ArtifactsFoundSuccess artifacts={artifacts?.content} />}
        {isLoadingArtifacts && (
          <div className=" flex items-center justify-center min-h-[192.5px] bg-zinc-900/30 border border-muted-foreground/30 rounded-xl">
            <LoadingPageSpinner />
          </div>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <GenericButton variant="secondary" type="button" label="Close" onClick={handleCloseModal} />

          {showFoundArtifacts && (
            <>
              <GenericButton variant="ghost" label="Upload New" onClick={() => setUploadNewArtifact(true)} />
              <GenericButton
                variant="default"
                type="button"
                label="Next"
                isLoading={isUploading || isLoadingArtifacts}
                onClick={() => onProceedWithExistingArtifacts(artifacts)}
              />
            </>
          )}

          {showCurrentArtifacts && (
            <GenericButton variant="ghost" label="Back to current" onClick={() => setUploadNewArtifact(false)} />
          )}

          {showUploadArtifact && (
            <GenericButton
              variant="default"
              type="submit"
              label={isUploading ? 'Uploading...' : 'Upload'}
              isLoading={isUploading || isLoadingArtifacts}
            />
          )}
        </div>
      </form>
    </FormProvider>
  );
};
